/**
 * Truck chassis simulation. Owned by `vehicle`.
 *
 * THIS IS AN ARCADE MODEL, NOT A SIMULATOR. It is a body-frame velocity model with a yaw-rate
 * target, a grip circle, and a lateral-force cap. That combination gives you three things a
 * proper tyre model makes hard:
 *
 *   1. Steering authority is a first-class knob (`yawResponse`) so input latency is tunable
 *      to the frame rather than emerging from tyre relaxation lengths.
 *   2. Drift is a state you enter deliberately (grip cap collapses under handbrake) and hold,
 *      not a stability failure you fall into.
 *   3. Every number in tuning.js maps to something a feel critic can ask for in plain English.
 *
 * Determinism: no wall clock, no Math.random, no Math.hypot (ARCHITECTURE.md §6). dt is
 * always 1/60. Every allocation happens in the constructor.
 */

import { TUNING } from './tuning.js';
import { clamp, clamp01, angleDelta, damp } from '../core/mathx.js';

const D = TUNING.dims;
const DR = TUNING.drive;
const H = TUNING.handling;
const S = TUNING.susp;
const T = TUNING.tilt;
const C = TUNING.collision;

/** Wheel contact slots. 0 FL, 1 FR, 2 RL, 3 RR. */
const CONTACTS = 4;

export class Chassis {
  constructor(ctx) {
    this.ctx = ctx;

    // ---- pose
    this.x = 0; this.y = 0; this.z = 0;
    this.yaw = 0;
    this.yawRate = 0;

    // ---- body-frame velocity
    this.vLong = 0;      // + forward
    this.vLat = 0;       // + to the truck's right
    this.vy = 0;

    // ---- world velocity (derived, published)
    this.vx = 0; this.vz = 0;

    // ---- state
    this.gear = 'D';
    this.slipAngle = 0;
    this.isDrifting = false;
    this.isAirborne = false;
    this.damage = 0;
    this.surface = 'road';
    this.gripMult = 1;
    this.steerAngle = 0;
    this.wheelSpin = 0;
    this.wheelSpinRate = 0;
    this.throttle01 = 0;
    this.brake01 = 0;
    this.handbrake01 = 0;
    /**
     * How far the handbrake is ENGAGED, 0..1 — not how far the key is down. Ramps in over
     * `handling.handbrakeEngageTime` of continuous hold and drops out ~3× faster. Every drift
     * knob reads this rather than the raw button, so a 25 ms stab cannot buy a full drift.
     * See the `handbrakeEngageTime` comment in tuning.js for the exploit it closes.
     */
    this.hbEngage01 = 0;
    this._hbPrev = 0;
    this._hbEdge = false;
    /** Low-passed surface gradient under the wheelbase, for the crest-launch test. */
    this._grade = 0;
    this._gradeValid = false;
    this._crestLock = 0;
    this.longAccel = 0;
    this.latAccel = 0;
    this.pitch = 0;
    this.roll = 0;
    this.heave = 0;
    this.groundY = 0;
    this.groundPitch = 0;
    this.groundRoll = 0;
    this.wheelspin01 = 0;

    // ---- per-wheel contacts (published on ctx.player)
    this.contacts = new Array(CONTACTS);
    for (let i = 0; i < CONTACTS; i++) {
      this.contacts[i] = { grounded: true, surface: 'road', slip: 0, compression: 0.5, groundY: 0 };
    }
    this._wheelLocal = [
      [D.trackHalf, D.axleFrontZ],
      [-D.trackHalf, D.axleFrontZ],
      [D.trackHalf, (D.axleRearZ + D.axleRear2Z) / 2],
      [-D.trackHalf, (D.axleRearZ + D.axleRear2Z) / 2],
    ];

    // ---- transient bookkeeping for events (read + cleared by VehicleSystem)
    this.ev = {
      landed: false, landedSpeed: 0, airtime: 0,
      launched: false, launchSpeed: 0,
      driftStart: false, driftEnd: false, driftDuration: 0, driftPeakSlip: 0, driftDistance: 0,
      surfaceFrom: '', surfaceChanged: false,
      gearChanged: false,
      collided: false, collideImpulse: 0, collideKind: 'static',
      cnx: 0, cny: 0, cnz: 0, cpx: 0, cpy: 0, cpz: 0,
    };

    this._airTime = 0;
    this._driftTime = 0;
    this._driftDist = 0;
    this._driftPeak = 0;
    this._brakeHold = 0;
    this._teleported = false;
    this._prevRestY = 0;
    this.surfaceProps = TUNING.surfaces.road;

    // Reusable collision query objects — no per-step allocation (ARCHITECTURE.md N9).
    this._sphere = { center: { x: 0, y: 0, z: 0 }, x: 0, y: 0, z: 0, radius: D.hullRadius };
    /**
     * Impulse inbox: a PREALLOCATED pool of `impulseSlots` records plus a count, not a queue
     * of fresh literals.
     *
     * ROUND 9 heap audit. `pushImpulse` is the one entry point traffic and gameplay use to
     * shove the truck, and it used to allocate `{x, y, z, kind}` on every call. That is a
     * per-frame allocation on a hot path by any reasonable reading of N9: the cap is 8 per
     * fixed step, so a truck grinding along a wall or a line of cones can mint up to 480
     * short-lived objects a second, every one of which becomes minor-GC pressure that shows
     * up in p99 and in `heapGrowthMB`. The slots are written in place and the count is reset
     * each step, so the steady-state allocation on this path is now exactly zero.
     */
    this._impulses = [];
    for (let i = 0; i < C.impulseSlots; i++) this._impulses.push({ x: 0, y: 0, z: 0, kind: 'static' });
    this._impulseCount = 0;
  }

  // ------------------------------------------------------------------ helpers

  get speed() { const v = this.vLong * this.vLong + this.vLat * this.vLat; return Math.sqrt(v); }
  get forwardX() { return Math.sin(this.yaw); }
  get forwardZ() { return Math.cos(this.yaw); }
  get rightX() { return Math.cos(this.yaw); }
  get rightZ() { return -Math.sin(this.yaw); }

  setPose(x, y, z, yaw, speed) {
    this.x = x; this.y = y; this.z = z;
    this.yaw = yaw;
    this.yawRate = 0;
    this.vLong = speed || 0;
    this.vLat = 0; this.vy = 0;
    this.slipAngle = 0;
    this.isDrifting = false;
    this._driftTime = 0; this._driftDist = 0; this._driftPeak = 0;
    this.pitch = 0; this.roll = 0; this.heave = 0;
    this.vy = 0;
    this.isAirborne = false;
    this._airTime = 0;
    this._prevRestY = y;
    this._grade = 0;
    this._gradeValid = false;
    this._crestLock = 0;
    this._teleported = true;
    this._impulseCount = 0;
    this.hbEngage01 = 0;
    this._hbPrev = 0;
    this._syncWorldVelocity();
  }

  /**
   * Other subsystems (traffic, gameplay cones) shove the truck through this.
   *
   * NON-FINITE IMPULSES ARE REJECTED, AND THIS IS NOT PARANOIA — it is the fix for the worst
   * bug the project has shipped. `traffic` called this with `applyImpulse(NaN, 0.05, NaN,
   * 'ped')` on contact with a pedestrian whose own position had gone NaN. The arithmetic then
   * did something genuinely nasty rather than failing loudly:
   *
   *   · `this.vx += NaN` poisons world velocity, which is what `ctx.player.velocity` publishes.
   *   · `mag` is NaN, and `NaN > impulse` is FALSE, so `impulse` stays 0 and the body-frame
   *     rebuild below never runs — so `_syncWorldVelocity()` silently heals vx/vz on the next
   *     step from vLong/vLat. Position keeps integrating normally.
   *   · So for hundreds of frames the truck looks fine while every consumer of
   *     `player.velocity` reads NaN. The chase camera is one of those consumers.
   *   · Eventually one frame lands where `impulse > 0` from a real contact in the same step,
   *     `vLong` is rebuilt from the NaN vx, and the whole state goes NaN permanently. The
   *     camera follows the truck to NaN, nothing is inside the frustum, and the player is
   *     left staring at flat sky with a working HUD over it.
   *
   * A subsystem must not be able to destroy the player through a published interface. Drop the
   * impulse and count it, so the failure is visible in stats instead of fatal on screen.
   */
  pushImpulse(x, y, z, kind) {
    if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) {
      this.rejectedImpulses = (this.rejectedImpulses || 0) + 1;
      this.lastRejectedImpulseKind = kind || 'static';
      return;
    }
    if (this._impulseCount >= C.impulseSlots) return;
    const q = this._impulses[this._impulseCount++];
    q.x = x; q.y = y; q.z = z; q.kind = kind || 'static';
  }

  _syncWorldVelocity() {
    const fx = this.forwardX, fz = this.forwardZ, rx = this.rightX, rz = this.rightZ;
    this.vx = fx * this.vLong + rx * this.vLat;
    this.vz = fz * this.vLong + rz * this.vLat;
  }

  // ------------------------------------------------------------------ step

  /** One fixed step. dt is ALWAYS 1/60. */
  step(dt, input) {
    const ev = this.ev;
    ev.landed = false; ev.launched = false; ev.driftStart = false; ev.driftEnd = false;
    ev.surfaceChanged = false; ev.gearChanged = false; ev.collided = false; ev.collideImpulse = 0;
    ev.collideKind = 'static';

    this.handbrake01 = input.handbrake;
    // Engagement state, resolved BEFORE anything reads it. `_hbEdge` is true for exactly the
    // one step on which the lever goes down, and is what `_longitudinal` bills per stab.
    const hbDown = input.handbrake > 0.5;
    this._hbEdge = hbDown && this._hbPrev < 0.5;
    this._hbPrev = hbDown ? 1 : 0;
    this.hbEngage01 = clamp01(hbDown
      ? this.hbEngage01 + dt / H.handbrakeEngageTime
      : this.hbEngage01 - dt / H.handbrakeReleaseTime);

    this._sampleSurface();
    this._updateGear(dt, input);

    /**
     * `core/input` publishes PHYSICAL pedal axes: W/right-trigger is the accelerator and
     * S/left-trigger is the brake/reverse pedal. Resolve them into ACTUATOR demand only after
     * `_updateGear`, because the same held S press that selects R must begin driving backward
     * on the transition step. In R the physical pedals exchange jobs: S drives and W brakes.
     *
     * Do not move this swap into `core/input`. Core does not own vehicle state, and making S a
     * global throttle would remove the player's forward brake. Keep these normalized fields as
     * the one source for physics, engine load, wheelspin and brake-lamp presentation.
     */
    const reversing = this.gear === 'R';
    this.throttle01 = reversing ? input.brake : input.throttle;
    this.brake01 = reversing ? input.throttle : input.brake;

    this._longitudinal(dt, input);
    this._steering(dt, input);
    this._lateral(dt, input);

    // Integrate planar motion
    this._syncWorldVelocity();
    this.x += this.vx * dt;
    this.z += this.vz * dt;

    this._vertical(dt);
    this._collide(dt);
    this._attitude(dt);
    this._drift(dt);
    this._wheels(dt);

    this.damage = clamp01(this.damage - C.damageDecay * dt);
    this._teleported = false;
  }

  // ---------------------------------------------------------------- surfaces

  _sampleSurface() {
    const g = this.ctx.world.sampleGround(this.x, this.z);
    let surf = g.surface || 'road';
    // Weather converts the road surface into something that fights you.
    const weather = this.ctx.gameplay.weather;
    if (weather === 'winter' && (surf === 'road' || surf === 'sidewalk')) surf = 'slush';
    if (this.surface !== surf) {
      this.ev.surfaceFrom = this.surface;
      this.ev.surfaceChanged = true;
      this.surface = surf;
    }
    const props = TUNING.surfaces[surf] || TUNING.surfaces.road;
    const wg = TUNING.weatherGrip[weather] !== undefined ? TUNING.weatherGrip[weather] : 1;
    this.surfaceProps = props;
    this.gripMult = props.grip * (surf === 'slush' ? 1 : wg);
    this.groundY = g.y;
  }

  // ---------------------------------------------------------------- gearbox

  _updateGear(dt, input) {
    const wasGear = this.gear;
    if (input.gearToggle) {
      this.gear = this.gear === 'D' ? 'R' : 'D';
      this._brakeHold = 0;
    } else {
      /**
       * HILL-HOLDING IS NOT A REQUEST FOR REVERSE.
       *
       * On a grade the ONLY way to be stopped is to hold something, so the auto-reverse timer
       * would fire every single time a player stopped on Mont Royal — silently putting them in
       * R, where W becomes the brake. `_slopeHeld` says the brakes are currently doing
       * hill-holding work rather than parking work, and suppresses the timer while they are.
       *
       * Deliberately NOT a blanket suppression on any grade: the truck can still be put in R on
       * a hill with the gear toggle, and the natural mountain manoeuvre — release, let it roll
       * back, steer — works because rolling backward in D is a legal state. On flat ground
       * `groundPitch` is 0, so `_slopeHeld` is false whenever the truck is genuinely stopped by
       * choice and every existing transition (including `npm run check:reverse`) is unchanged.
       */
      const stopped = Math.abs(this.vLong) < DR.stopSpeed;
      if (stopped && this._hillHeld && input.brake > 0.5) this._brakeHold = 0;
      else if (stopped && input.brake > 0.5 && input.throttle < 0.1) {
        this._brakeHold += dt;
        if (this._brakeHold > DR.autoReverseHold && this.gear === 'D') this.gear = 'R';
      } else if (stopped && input.throttle > 0.5 && this.gear === 'R') {
        this.gear = 'D';
        this._brakeHold = 0;
      } else {
        this._brakeHold = 0;
      }
    }
    if (this.gear !== wasGear) this.ev.gearChanged = true;
  }

  // ---------------------------------------------------------------- long axis

  _longitudinal(dt, input) {
    const props = this.surfaceProps;
    const dir = this.gear === 'D' ? 1 : -1;
    const vmax = this.gear === 'D' ? DR.maxSpeed : DR.maxSpeedReverse;
    const throttle = this.throttle01;
    const brake = this.brake01;
    const before = this.vLong;

    if (!this.isAirborne) {
      // --- engine. Front-loaded: full shove off the line, tapering hard near vmax.
      if (throttle > 0.001) {
        const sNorm = clamp01((this.vLong * dir) / vmax);
        let a = DR.accelBase * (1 - Math.pow(sNorm, DR.accelExp));
        a += DR.launchBoost * (1 - clamp01(Math.abs(this.vLong) / DR.launchRefSpeed));
        if (dir < 0) a *= DR.reverseAccelScale;
        a *= throttle * props.accel * (0.55 + 0.45 * this.gripMult);
        // Wheelspin: on a slick surface the power does not all reach the road.
        this.wheelspin01 = clamp01((1 - this.gripMult) * throttle * (1 - sNorm) * 1.6);
        this.vLong += dir * a * dt;
      } else {
        this.wheelspin01 *= 0.85;
      }

      // --- brakes. Strong and predictable; this is a Crazy Taxi stopping distance.
      if (brake > 0.001 && Math.abs(this.vLong) > 0.02) {
        const bd = DR.brakeDecel * brake * props.brake * this.gripMult;
        const s = this.vLong > 0 ? 1 : -1;
        this.vLong -= s * bd * dt;
        if ((this.vLong > 0) !== (s > 0)) this.vLong = 0;
      }
      if (input.handbrake > 0.001 && Math.abs(this.vLong) > 0.02) {
        const bd = DR.handbrakeDecel * input.handbrake * props.brake;
        const s = this.vLong > 0 ? 1 : -1;
        this.vLong -= s * bd * dt;
        if ((this.vLong > 0) !== (s > 0)) this.vLong = 0;
      }
      // Billed PER ENGAGEMENT, not per frame held. A cost that only accrues while the lever
      // is down is dodged by not holding it down; this one is not. See tuning.js
      // `drive.handbrakeEngageScrub`.
      if (this._hbEdge && Math.abs(this.vLong) > 0.02) {
        const s = this.vLong > 0 ? 1 : -1;
        this.vLong -= s * Math.min(Math.abs(this.vLong), DR.handbrakeEngageScrub * props.brake);
      }

      /**
       * --- coast + aero
       *
       * `coastDecel` is ENGINE BRAKING, and engine braking dies with the driveline. It used to
       * be a flat 2.4 m/s² right down to a standstill, which made it a 25 %-GRADE PARKING
       * BRAKE: a stationary truck could not roll away from anything shallower, because gravity
       * along a 12 % street is only 1.17 m/s². Fading it in over `coastFadeSpeed` is what lets
       * a Mont Royal street take the truck away from you, and it is also what stops the roll
       * from juddering — without the fade the truck alternates between gravity nudging it past
       * the 0.02 m/s gate and the flat coast term slamming it back to zero, every other frame.
       *
       * Above `coastFadeSpeed` (14.4 km/h) nothing changes, so lift-off feel at driving speed
       * and every graded shot are untouched. Below it the truck rolls to a stop over about a
       * metre instead of stopping dead, which is the regime where a hill should be in charge.
       */
      const coastFade = clamp01(Math.abs(this.vLong) / DR.coastFadeSpeed);
      const coast = (throttle > 0.05 ? 0 : DR.coastDecel * coastFade)
        + DR.dragQuad * this.vLong * this.vLong;
      if (Math.abs(this.vLong) > 0.02) {
        const s = this.vLong > 0 ? 1 : -1;
        this.vLong -= s * coast * dt;
        if ((this.vLong > 0) !== (s > 0)) this.vLong = 0;
      }

      this._slope(dt, input, props);
    } else {
      // Airborne: only aero.
      this.vLong -= Math.sign(this.vLong) * DR.dragQuad * this.vLong * this.vLong * dt;
      this.wheelspin01 *= 0.9;
    }

    this.vLong = clamp(this.vLong, -DR.maxSpeedReverse, DR.maxSpeed);
    this.longAccel = (this.vLong - before) / dt;
  }

  /**
   * ═══ GRAVITY ALONG THE GRADE ═══
   *
   * The grounded model had no slope term at all: gravity existed only on `vy` while airborne,
   * so the truck climbed a 12 % street at full flat-road acceleration and descended one with no
   * gain. On a drivable Mont Royal that is the difference between a mountain and a painted
   * backdrop.
   *
   * THE PITCH COMES FROM THE WHEELBASE, NOT FROM A POINT NORMAL. `_vertical()` already samples
   * the ground under all four contact patches and derives `groundPitch` from the front/rear
   * height delta over the wheelbase. Using that instead of `sampleGround().normal` matters:
   * a point normal at a kerb, a pothole lip or the road crown reads as a cliff and would fire
   * a hard longitudinal kick every time a wheel crossed one, whereas a 4.6 m baseline averages
   * those out and only responds to real terrain. It costs nothing — the samples are already
   * taken — and it is one fixed step old, which at 1/60 s of terrain gradient is not a
   * measurable error.
   *
   * Sign: `groundPitch` is positive nose-up, so climbing gives a negative term. It vanishes
   * exactly at zero pitch, so flat ground is bit-identical to before. It is deliberately
   * outside the gear logic — gravity pulls downhill whichever way the truck is facing, so
   * backing up a hill is slowed by the same term that slows driving up it.
   *
   * ═══ AND THE STATIC HOLD, WHICH IS THE OTHER HALF ═══
   *
   * A slope term with no hold makes a stopped truck creep forever, and worse, chatter: the
   * brake block is gated on `|vLong| > 0.02`, so gravity nudges the truck past the gate, the
   * brake slams it back through zero, and it buzzes. Real brakes hold statically or they slip.
   * So: below `holdSpeed`, if the retardation available exceeds the pull of the grade, the
   * truck is PINNED at exactly zero and the slope term is skipped entirely.
   *
   * The available retardation is the best of the service brake, the handbrake, and
   * `parkResist` — the standing rolling resistance that exists with nothing held at all.
   * `parkResist` is what decides which streets a parked truck rolls down: 0.44 m/s² holds
   * anything under about 4.5 %, which covers the whole Plateau (the city swell tops out near
   * 1.2 %) and lets every Mont Royal street above 5 % take the truck away. That threshold is
   * why the graded shots cannot drift: `pothole-fill` parks the truck on ground with a
   * gradient of effectively zero.
   */
  _slope(dt, input, props) {
    this.slopeAccel = -S.gravity * Math.sin(this.groundPitch) * DR.slopeGravity;
    const hold = Math.max(
      DR.parkResist,
      this.brake01 * DR.brakeDecel * props.brake * this.gripMult,
      input.handbrake * DR.handbrakeDecel * props.brake,
    );
    /**
     * THE HOLD MUST YIELD TO THE THROTTLE, or it is a handbrake that is always on. Without the
     * `throttle01` gate the truck cannot pull away from a standstill ANYWHERE, on any surface:
     * one step of engine accel is 10.2/60 = 0.17 m/s, which is under `holdSpeed`, so the hold
     * zeroes it again on the very next step and the truck sits at 0 km/h under full power
     * forever. Measured exactly that before the gate went in — flat-ground top speed 0.0 km/h.
     */
    this._slopeHeld = this.throttle01 <= 0.02
      && Math.abs(this.vLong) < DR.holdSpeed
      && Math.abs(this.slopeAccel) <= hold;
    /**
     * `_slopeHeld` is true of a truck standing still on FLAT ground too — `parkResist` alone
     * holds it. `_hillHeld` is the narrower claim the gearbox needs: the brakes are holding a
     * grade that `parkResist` could not have held on its own, so the truck would roll if the
     * player let go. On flat ground this is always false, which is what keeps `_updateGear`
     * and `npm run check:reverse` bit-identical to before.
     */
    this._hillHeld = this._slopeHeld && Math.abs(this.slopeAccel) > DR.parkResist;
    if (this._slopeHeld) this.vLong = 0;
    else this.vLong += this.slopeAccel * dt;
  }

  // ---------------------------------------------------------------- steering

  _steering(dt, input) {
    const speedAbs = Math.abs(this.vLong);
    // Lock tapers with speed so the truck is not twitchy at 120 km/h but can still park.
    const t = clamp01(speedAbs / H.steerFalloffSpeed);
    const lock = H.steerMax + (H.steerMaxHighSpeed - H.steerMax) * (t * t * (3 - 2 * t));
    /**
     * SIGN: the truck's local +X is SCREEN-LEFT from the chase camera (three.js is
     * right-handed and the camera's local +X — screen right — maps to world −X when you are
     * looking along +Z). So a positive "steer right" input must DECREASE yaw. One negation
     * here fixes both the yaw integration and the steered front-wheel meshes, which read
     * this value directly.
     */
    this.steerAngle = -lock * input.steer;

    let target;
    if (this.isAirborne) {
      target = -input.steer * H.airYawRate;
      this.yawRate = damp(this.yawRate, target, H.yawResponseAir, dt);
      this.yaw += this.yawRate * dt;
      return;
    }

    // Bicycle model: the geometric yaw rate the front wheels are asking for.
    target = (this.vLong / D.wheelbase) * Math.tan(this.steerAngle);

    // ENGAGEMENT, not the button. `hb01` is 1 only after the lever has been held for
    // `handbrakeEngageTime`; a stab gets a fraction of the drift. tuning.js documents why.
    const hb01 = this.hbEngage01;
    target *= 1 + (H.handbrakeYawBoost - 1) * hb01;

    // Grip circle clamp: you cannot rotate faster than the tyres can hold... unless the
    // handbrake is down, in which case you very much can. That IS the drift.
    const gripBudget = H.maxLatAccel * this.gripMult;
    const latBudget = gripBudget + (H.driftYawAccel - gripBudget) * hb01;
    const maxYaw = latBudget / Math.max(speedAbs, 3.2);
    target = clamp(target, -maxYaw, maxYaw);

    // Self-aligning torque brings the nose back to the velocity vector when you stop asking
    // for yaw. This is what makes a drift recoverable inside 0.8 s.
    // (velocity heading == yaw + slipAngle, so nudging yaw by +slip aligns the nose with
    // where the truck is actually going.)
    if (hb01 < 1 && Math.abs(input.steer) < H.recoverySteerWindow && speedAbs > 2) {
      target += this.slipAngle * H.selfAlign * (1 - hb01);
    }

    // Parking pivot: at walking pace, asking for drive or brake still rotates the truck.
    // This is what stops a nose-in-to-a-wall wedge from being terminal.
    const drive = Math.max(input.throttle, input.brake);
    if (speedAbs < H.pivotSpeed && drive > 0.1) {
      const fade = 1 - speedAbs / H.pivotSpeed;
      const pivot = -input.steer * H.pivotYawRate * drive * fade;
      if (Math.abs(pivot) > Math.abs(target)) target = pivot;
    }

    this.yawRate = damp(this.yawRate, target, H.yawResponse, dt);
    this.yaw += this.yawRate * dt;
    if (this.yaw > Math.PI) this.yaw -= Math.PI * 2;
    else if (this.yaw < -Math.PI) this.yaw += Math.PI * 2;
  }

  // ---------------------------------------------------------------- lateral

  _lateral(dt, input) {
    const hb01 = this.hbEngage01;

    if (this.isAirborne) {
      this.latAccel = 0;
      this.slipAngle = Math.atan2(this.vLat, Math.max(Math.abs(this.vLong), 0.5));
      return;
    }

    // Body-frame coupling: yawing the chassis throws the mass sideways.
    const beforeLat = this.vLat;
    this.vLat -= this.yawRate * this.vLong * dt;
    this.vLong += this.yawRate * this.vLat * dt * 0.35;

    // Tyre lateral force, capped by the grip circle. The rear grip collapses in step with the
    // ENGAGEMENT, so the yaw budget above and the scrub cap here open together — the whole
    // exploit was that a stab opened the first without opening the second.
    let cap = H.maxLatAccel * this.gripMult;
    cap *= 1 - (1 - H.handbrakeLatGrip) * hb01;
    if (hb01 < 1 && Math.abs(input.steer) < H.recoverySteerWindow) {
      cap *= 1 + (H.recoveryGripBoost - 1) * (1 - hb01);
    }
    // Longitudinal demand eats into the friction budget (combined slip, cheaply).
    const longDemand = clamp01((Math.abs(this.longAccel) / DR.brakeDecel) * 0.55);
    cap *= 1 - longDemand * 0.28;

    const want = H.latGripRate * Math.abs(this.vLat);
    const a = Math.min(want, cap);
    const s = this.vLat > 0 ? 1 : -1;
    const dv = a * dt;
    this.vLat = Math.abs(this.vLat) <= dv ? 0 : this.vLat - s * dv;
    this.latAccel = (this.vLat - beforeLat) / dt;

    /**
     * SCRUB DRAG — ROUND 11 game-feel adjudication: *"Handbrake + full lock at 89.1 km/h EXITS
     * AT 99.6 km/h — faster than it entered. Steering-only at 80 km/h reaches 43° slip and
     * exits at 86. There is no reason to ever lift."*
     *
     * The cause is an absence, not a wrong number: nothing in this model charged the
     * longitudinal axis for cornering. A tyre generating lateral force is simultaneously
     * dragging along its own axis, and the harder it works the more it drags — that is what
     * makes braking into a corner a decision instead of a waste of time. Without it, full
     * throttle through a turn nets positive and the fastest line is the one that never lifts.
     *
     * THE PROXY MATTERS, and the obvious one is wrong. The first attempt charged the drag
     * against `a` — the lateral acceleration this step's SCRUB correction produces — and moved
     * the measured handbrake turn only from −12.3 % to −10.3 %, because a gripping turn has
     * almost no scrub: the steering-only case peaks at **6.5°** of slip, so `vLat` is tiny and
     * so is `a`. What a corner actually loads the tyres with is the CENTRIPETAL demand
     * `|yawRate · vLong|` — the force needed to hold the line at all, which is large at zero
     * slip and is exactly zero in a straight line. That keeps the promise that costs nothing to
     * drive fast in a straight line, so the 0→100 and top-speed numbers the same report scored
     * 8/10 do not move.
     */
    const scrub = Math.abs(this.yawRate * this.vLong) * H.scrubDrag * dt;
    if (this.vLong > 0) this.vLong = Math.max(0, this.vLong - scrub);
    else if (this.vLong < 0) this.vLong = Math.min(0, this.vLong + scrub);

    // Hard cap on slip so the truck never spins like a top.
    let maxLat = Math.max(Math.abs(this.vLong), 1.0) * Math.tan(H.maxSlip);

    /**
     * ═══ THE TRUCK MAY NOT TRAVEL FASTER SIDEWAYS THAN IT CAN DRIVE. ═══
     *
     * `_longitudinal` clamps `vLong` to `drive.maxSpeed`, but nothing clamped the RESULTANT.
     * The slip cap above is `tan(52°) = 1.28` times `vLong`, so `speed` — which is what the
     * HUD prints, what drives the FOV swell, the speed buzz and the engine note, and what
     * every gameplay speed gate reads — could legitimately reach 1.62 × the ceiling.
     *
     * The space-spam exploit lived in that gap: it pinned `vLong` near the ceiling and
     * ratcheted `vLat` up underneath it, and the speedometer read 149.4 km/h against a
     * 133.9 km/h design ceiling. Bounding the resultant makes the number honest.
     *
     * It cannot deaden a drift, because a real drift has already spent the longitudinal speed
     * that would make this bind: measured over the round-11 full-lock 90° turn from 89.5 km/h
     * `vLong` is 27 km/h by the time slip is at the 52° cap, where this allows 131 km/h of
     * lateral. It binds only above the ceiling, which is to say only on the exploit.
     */
    const lateralCeiling = DR.maxSpeed * DR.maxSpeed - this.vLong * this.vLong;
    if (lateralCeiling <= 0) maxLat = 0;
    else maxLat = Math.min(maxLat, Math.sqrt(lateralCeiling));
    this.vLat = clamp(this.vLat, -maxLat, maxLat);

    this.slipAngle = Math.atan2(this.vLat, Math.max(Math.abs(this.vLong), 0.5));
  }

  // ---------------------------------------------------------------- vertical

  _vertical(dt) {
    if (this._crestLock > 0) this._crestLock -= dt;
    const world = this.ctx.world;
    const fx = this.forwardX, fz = this.forwardZ, rx = this.rightX, rz = this.rightZ;

    let sum = 0, maxY = -Infinity;
    let frontY = 0, rearY = 0, leftY = 0, rightYy = 0;
    for (let i = 0; i < CONTACTS; i++) {
      const l = this._wheelLocal[i];
      const wx = this.x + rx * l[0] + fx * l[1];
      const wz = this.z + rz * l[0] + fz * l[1];
      const g = world.sampleGround(wx, wz);
      const c = this.contacts[i];
      c.groundY = g.y;
      c.surface = g.surface || 'road';
      sum += g.y;
      if (g.y > maxY) maxY = g.y;
      if (i < 2) frontY += g.y * 0.5; else rearY += g.y * 0.5;
      if (i === 0 || i === 2) rightYy += g.y * 0.5; else leftY += g.y * 0.5;
    }
    const avgY = sum / CONTACTS;
    const restY = avgY;

    this.groundPitch = Math.atan2(frontY - rearY, D.wheelbase);
    this.groundRoll = Math.atan2(rightYy - leftY, D.trackHalf * 2);

    const wasAirborne = this.isAirborne;

    // Never sink through the wheel plane.
    if (this.y < restY) { this.y = restY; if (this.vy < 0) this.vy = 0; }

    // Ground rising under the truck faster than the suspension can swallow → launch.
    // (A kerb at 25 m/s lifts the wheel plane ~7 m/s; a smooth ramp lifts almost nothing.)
    const prevRestY = this._prevRestY;
    const riseRate = (restY - prevRestY) / dt;
    this._prevRestY = restY;
    /**
     * A KERB IS A STEP; A HILL IS NOT. This rule fired on the raw rise rate, which was fine
     * while the only relief in the world was one 2.45 m gaussian hump — but a steady 12 %
     * CLIMB lifts the wheel plane at 0.12 × v, i.e. past the 1.5 m/s threshold at just 45 km/h.
     * Measured on a synthetic Mont Royal approach at 90 km/h: the truck launched every ~0.27 s
     * for the entire climb, 7 hops before it even reached the summit. It is the exact mistake
     * the crest test below documents, on the other sign.
     *
     * `this._grade` is the smoothed surface gradient (updated below, so this reads last step's
     * value — the steady state established so far). Subtracting the rise it predicts leaves
     * only the part the terrain did NOT lead you to expect, which is precisely what a kerb,
     * a ramp lip or a pothole edge is. A constant grade of any steepness contributes zero.
     */
    const expectedRise = this._grade * Math.sqrt(this.vx * this.vx + this.vz * this.vz);
    const excessRise = riseRate - expectedRise;
    if (!this.isAirborne && !this._teleported && excessRise > 1.5) {
      const lift = Math.min(excessRise * S.launchGain, S.maxLaunchVy);
      if (lift > this.vy) this.vy = lift;
    }

    /**
     * ═══ CREST LAUNCH — the ground falling away, which is the other half of a jump. ═══
     *
     * The rule above only ever fired on ground RISING under the truck: a kerb or a ramp face.
     * A summit crest does the opposite — the road drops away — and the body spring simply
     * followed it down, so cresting Mont Royal at 90 km/h produced no air at all.
     *
     * THE TEST IS PATH CURVATURE, AND TWO WRONG VERSIONS OF IT COST A MEASUREMENT EACH:
     *
     *   1. `riseRate < -k` — the ground falling away at some rate. But a CONSTANT GRADE does
     *      that as soon as you are quick enough. It fired continuously all the way down a
     *      steady 12 % street above 78 km/h, so the truck strobed in and out of the air 10
     *      times in 8 s, lost the engine every time it was airborne, and a 12 % descent under
     *      full throttle came back SLOWER (49.1 km/h) than a 6 % one (130.1).
     *   2. `d(riseRate)/dt < -k`. Closer, but `riseRate = grade · v`, so differencing it in
     *      TIME picks up `grade · dv/dt` — the truck's own acceleration. Pulling away hard up
     *      a steady 20 % grade produced 0.20 × 12 = 2.4 m/s² of phantom curvature and launched
     *      on flat-graded road again: 20 % and 25 % descents collapsed to 28.8 and 23.4 km/h.
     *
     * The quantity that actually decides it is geometric and has nothing to do with the
     * vehicle: how fast the SURFACE GRADIENT changes per metre TRAVELLED, times v². Difference
     * the grade against distance, not against time, and the acceleration term cancels — a
     * constant grade reads exactly zero curvature at any speed under any throttle. The v²
     * scaling is the real physics and hands us the speed gate for free: the same crest is
     * driven over at 40 km/h and jumped at 90.
     *
     * The grade is low-passed (`crestSmooth`) because a raw second difference of sampled
     * terrain spikes on a kerb lip or a pothole edge and would fire a phantom launch; the
     * kerb-launch rule above reads the RAW rate and still gets its spike. `vy` is floored at
     * zero rather than inherited, because the spring is pushing DOWN at the moment of release
     * and letting that through would fire the truck into the road.
     */
    const ds = Math.sqrt(this.vx * this.vx + this.vz * this.vz) * dt;
    if (ds <= S.crestMinStep) {
      // Barely moving: the grade estimate is a division by noise, and whatever the filter is
      // holding is stale by the time the truck moves again. Re-seed on the next real step.
      this._gradeValid = false;
    } else {
      const grade = (restY - prevRestY) / ds;
      if (!this._gradeValid) {
        /**
         * SEED, DO NOT FILTER, ON THE FIRST MOVING STEP. The filter converging from 0 to a real
         * grade IS a grade change, and the test cannot tell that apart from a crest — measured,
         * a truck simply placed on a steady 12 % descent at 120 km/h launched on the start-up
         * transient (`(0 − 0.06) × (1 − e^−25/60) / 0.55 m × 33 m/s² ≈ −40 m/s²`), then chain-
         * bounced on `landBounce` and spent 1200 of 1800 frames in the air.
         */
        this._grade = grade;
        this._gradeValid = true;
      } else {
        const gSmooth = damp(this._grade, grade, S.crestSmooth, dt);
        const curveAccel = ((gSmooth - this._grade) / ds) * (ds / dt) * (ds / dt);
        this._grade = gSmooth;
        /**
         * ONE JUMP PER CREST. A summit is curved for its whole length, so without a lockout the
         * truck launches, lands 20 m later still on the curve, and launches again — measured 10
         * to 16 separate launches crossing one 12 % summit, which is not a jump, it is a stutter,
         * and every one of them fires `vehicle:airborne` / `vehicle:landed` on the bus for `fx`
         * and `audio` to react to.
         */
        if (!this.isAirborne && !this._teleported && this._crestLock <= 0
          && gSmooth < -S.crestMinGrade && curveAccel < -S.crestFallAccel) {
          this.isAirborne = true;
          this._airTime = 0;
          this._crestLock = S.crestLockout;
          if (this.vy < 0) this.vy = 0;
        }
      }
    }

    if (this.isAirborne) {
      this.vy -= S.gravity * dt;
      this.y += this.vy * dt;
      this._airTime += dt;
      if (this.y <= restY) {
        /**
         * Touchdown. IMPACT IS MEASURED AGAINST THE SURFACE, NOT AGAINST THE WORLD — the road
         * is moving too, and on a descent it is moving down fast.
         *
         * Absolute `-vy` made a steep descent self-sustaining: land, take a `landBounce`
         * rebound of absolute `vy`, and by the time the truck comes back down the road has
         * dropped another 3.2 m/s × the hop, so it lands harder than it took off and bounces
         * again. Measured on a 12 % descent after a crest jump — 15-plus rebounds, 13 s of a
         * 15 s run spent airborne, and a hard-landing `fx.shake` on most of them.
         *
         * `riseRate` is the wheel plane's own vertical velocity, so `riseRate − vy` is the
         * closing speed: zero when the truck is already tracking the road's descent, which is
         * exactly when it should settle rather than bounce. Rebounding to `riseRate + …` leaves
         * the truck matching the surface instead of being handed a phantom hop. On flat ground
         * `riseRate` is 0 and every one of these lines is identical to what it replaced.
         */
        this.y = restY;
        const impact = Math.max(0, riseRate - this.vy);
        this.vy = impact > 1.0 ? riseRate + impact * S.landBounce : riseRate;
        this.isAirborne = (this.vy - riseRate) > 0.9;
        if (!this.isAirborne) {
          this.ev.landed = true;
          this.ev.landedSpeed = impact;
          this.ev.airtime = this._airTime;
          this._airTime = 0;
        }
      }
    } else {
      // Grounded: spring the body toward the wheel plane.
      const err = restY - this.y;
      this.vy += (err * S.springK - this.vy * S.damping) * dt;
      this.y += this.vy * dt;
      if (this.y < maxY - 0.02) { this.y = maxY - 0.02; if (this.vy < 0) this.vy = 0; }
      if (this.y > restY + S.airborneClearance && this.vy > 0.5) {
        this.isAirborne = true;
        this._airTime = 0;
      }
    }

    if (!wasAirborne && this.isAirborne) {
      this.ev.launched = true;
      this.ev.launchSpeed = this.speed;
    }

    // Per-wheel compression for the visible suspension travel.
    for (let i = 0; i < CONTACTS; i++) {
      const c = this.contacts[i];
      const l = this._wheelLocal[i];
      // Body height at this corner, including attitude.
      const cornerY = this.y - this.pitch * l[1] + this.roll * l[0];
      const gap = cornerY - c.groundY;
      c.compression = clamp01(1 - (gap - (D.rideHeight - S.travel * 0.5)) / S.travel);
      c.grounded = !this.isAirborne && gap < D.rideHeight + S.travel;
      c.slip = Math.abs(this.slipAngle) + this.wheelspin01 * 0.4;
    }
  }

  // ---------------------------------------------------------------- collision

  _collide(dt) {
    const world = this.ctx.world;
    let impulse = 0;
    let nx = 0, ny = 0, nz = 0;
    let px = this.x, py = this.y + D.hullY, pz = this.z;
    const fx = this.forwardX, fz = this.forwardZ;

    // Contacts are AGGREGATED across the three hull spheres and resolved once. Resolving
    // each contact independently compounds the restitution and the tangential scrub — a
    // glancing kerb graze was killing 45% of the truck's speed in a single step.
    let pushX = 0, pushZ = 0, pushY = 0;
    let sumNx = 0, sumNy = 0, sumNz = 0, contacts = 0;
    const climbCeiling = this._prevRestY + C.maxClimb;

    if (!this.noCollide) {
      for (let s = 0; s < 3; s++) {
        const off = s === 0 ? D.hullSpan : (s === 1 ? 0 : -D.hullSpan);
        const sx = this.x + fx * off;
        const sz = this.z + fz * off;
        const sp = this._sphere;
        sp.x = sx; sp.y = this.y + D.hullY; sp.z = sz;
        sp.center.x = sx; sp.center.y = sp.y; sp.center.z = sz;
        sp.radius = D.hullRadius;

        const hits = world.collide(sp);
        if (!hits || !hits.length) continue;

        for (let i = 0; i < hits.length; i++) {
          const h = hits[i];
          const n = h.normal || h;
          let hx = n.x || 0, hy = n.y || 0, hz = n.z || 0;
          const len = Math.sqrt(hx * hx + hy * hy + hz * hz);
          if (len < 1e-5) continue;
          hx /= len; hy /= len; hz /= len;
          const depth = Math.min(h.depth || 0, C.maxPushPerStep);
          if (depth <= 0) continue;

          // Vertical-ish contacts feed the suspension, not the bumper.
          if (hy > 0.7) { if (depth > pushY) pushY = depth; continue; }

          // Take the largest push along each axis rather than summing duplicates.
          const dx = hx * depth, dz = hz * depth;
          if (Math.abs(dx) > Math.abs(pushX)) pushX = dx;
          if (Math.abs(dz) > Math.abs(pushZ)) pushZ = dz;
          sumNx += hx * depth; sumNy += hy * depth; sumNz += hz * depth;
          contacts++;
          px = sx + hx * D.hullRadius; py = this.y + D.hullY; pz = sz + hz * D.hullRadius;
        }
      }
    }

    // Refuse upward separation past the climb ceiling — otherwise a run of vertical contacts
    // walks the truck up a facade and beaches it on the roof.
    if (pushY > 0 && this.y < climbCeiling) {
      this.y = Math.min(this.y + pushY, climbCeiling);
      if (this.vy < 0) this.vy = 0;
    }

    if (contacts > 0) {
      const nl = Math.sqrt(sumNx * sumNx + sumNy * sumNy + sumNz * sumNz);
      if (nl > 1e-5) {
        const hx = sumNx / nl, hy = sumNy / nl, hz = sumNz / nl;
        this.x += pushX;
        this.z += pushZ;
        const vn = this.vx * hx + this.vz * hz;
        if (vn < 0) {
          const j = -vn * (1 + C.restitution);
          this.vx += hx * j;
          this.vz += hz * j;
          // Scrub a little tangential speed so scraping a wall costs you something.
          const along = this.vx * hx + this.vz * hz;
          this.vx -= (this.vx - hx * along) * C.tangentLoss;
          this.vz -= (this.vz - hz * along) * C.tangentLoss;
          impulse = -vn;
          nx = hx; ny = hy; nz = hz;
        }
      }
    }

    // External shoves (traffic, cones) arrive through the same path.
    while (this._impulseCount > 0) {
      const q = this._impulses[--this._impulseCount];
      this.vx += q.x; this.vy += q.y; this.vz += q.z;
      const mag = Math.sqrt(q.x * q.x + q.y * q.y + q.z * q.z);
      if (mag > impulse) { impulse = mag; this.ev.collideKind = q.kind; nx = -q.x; ny = -q.y; nz = -q.z; }
    }

    if (impulse > 0) {
      // Push world velocity back into the body frame.
      const f_x = this.forwardX, f_z = this.forwardZ, r_x = this.rightX, r_z = this.rightZ;
      this.vLong = this.vx * f_x + this.vz * f_z;
      this.vLat = this.vx * r_x + this.vz * r_z;
      // Never leave the player dead in the road: keep a little momentum in the free direction.
      if (Math.abs(this.vLong) < C.minExitSpeed && impulse > 4) {
        this.vLong = (this.vLong >= 0 ? 1 : -1) * C.minExitSpeed;
      }
      // A hit unsettles the truck: some of the impulse becomes yaw.
      this.yawRate += clamp((nx * f_z - nz * f_x) * impulse * C.yawKick, -C.maxYawKick, C.maxYawKick);
      this.damage = clamp01(this.damage + impulse * C.damagePerImpulse);

      if (impulse > C.minEventImpulse) {
        const ev = this.ev;
        ev.collided = true;
        ev.collideImpulse = impulse;
        const nl = Math.sqrt(nx * nx + ny * ny + nz * nz) || 1;
        ev.cnx = nx / nl; ev.cny = ny / nl; ev.cnz = nz / nl;
        ev.cpx = px; ev.cpy = py; ev.cpz = pz;
      }
    }

    // Absolute backstop: never leave the world.
    const b = world.bounds;
    if (b) {
      if (this.x < b.minX + 2) { this.x = b.minX + 2; if (this.vx < 0) { this.vx = -this.vx * 0.4; this.vLong *= 0.6; } }
      if (this.x > b.maxX - 2) { this.x = b.maxX - 2; if (this.vx > 0) { this.vx = -this.vx * 0.4; this.vLong *= 0.6; } }
      if (this.z < b.minZ + 2) { this.z = b.minZ + 2; if (this.vz < 0) { this.vz = -this.vz * 0.4; this.vLong *= 0.6; } }
      if (this.z > b.maxZ - 2) { this.z = b.maxZ - 2; if (this.vz > 0) { this.vz = -this.vz * 0.4; this.vLong *= 0.6; } }
    }
  }

  // ---------------------------------------------------------------- attitude

  /**
   * Body roll / pitch / heave. Pure juice: this is free game feel and it is deliberately
   * exaggerated. A truck that does not lean does not read as heavy.
   */
  _attitude(dt) {
    // +roll lifts the truck's RIGHT side. Turning right throws the mass left, so the right
    // side comes up: roll follows the centripetal demand (yawRate * vLong) directly.
    let targetRoll = (this.yawRate * this.vLong) * T.rollPerLat;
    targetRoll -= this.slipAngle * T.rollFromSlip;   // a slide leans the body to the outside
    targetRoll = clamp(targetRoll, -T.maxRoll, T.maxRoll);

    // +pitch is nose-DOWN. Braking dives, throttle squats.
    let targetPitch = -this.longAccel * T.pitchPerLong;
    if (this.isAirborne) targetPitch -= clamp(this.vy * S.airPitchRate, -0.10, 0.10);
    targetPitch = clamp(targetPitch, -T.maxPitch, T.maxPitch);

    const targetHeave = clamp(-Math.abs(this.longAccel) * T.heavePerLong, -T.maxHeave, 0);

    const r = this._teleported ? 1 : 1 - Math.exp(-T.response * dt);
    this.roll += (targetRoll + this.groundRoll - this.roll) * r;
    this.pitch += (targetPitch - this.groundPitch - this.pitch) * r;
    this.heave += (targetHeave - this.heave) * r;
  }

  // ---------------------------------------------------------------- drift fsm

  _drift(dt) {
    const slip = Math.abs(this.slipAngle);
    const fast = Math.abs(this.vLong) > H.driftMinSpeed;
    if (!this.isDrifting) {
      if (fast && slip > H.driftEnterSlip && !this.isAirborne) {
        this.isDrifting = true;
        this._driftTime = 0; this._driftDist = 0; this._driftPeak = slip;
        this.ev.driftStart = true;
      }
    } else {
      this._driftTime += dt;
      this._driftDist += Math.abs(this.speed) * dt;
      if (slip > this._driftPeak) this._driftPeak = slip;
      if (slip < H.driftExitSlip || !fast) {
        this.isDrifting = false;
        this.ev.driftEnd = true;
        this.ev.driftDuration = this._driftTime;
        this.ev.driftPeakSlip = this._driftPeak;
        this.ev.driftDistance = this._driftDist;
      }
    }
  }

  // ---------------------------------------------------------------- wheels

  _wheels(dt) {
    const rate = clamp(this.vLong / D.wheelRadius, -TUNING.fxbits.maxWheelSpinRate, TUNING.fxbits.maxWheelSpinRate);
    const spin = rate + this.wheelspin01 * 18 * Math.sign(this.vLong || 1);
    this.wheelSpinRate = spin;
    this.wheelSpin += spin * dt;
    if (this.wheelSpin > Math.PI * 2) this.wheelSpin -= Math.PI * 2;
    else if (this.wheelSpin < -Math.PI * 2) this.wheelSpin += Math.PI * 2;
  }
}
